import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue'
import { v4 as uuid } from 'uuid'
import { useMaps } from './maps'
import { postSegmentLines } from '../scripts/fieldsPlanning'
import { useDrawingStore } from './drawing'
import i18n from '@/i18n'
import * as turf from '@turf/turf'

export const useSegmentsStore = defineStore('segments', {
    state: () => ({
        interactionPoints: [],        // markers on the map
        interactionPointFeatures: [], // features from the API
        linesOfPoint: [],
        allSegmentsLines: [],
        allSegmentsLinesCoords: [],
        infoWindows: [],
        okMarkers: [],
        selectedPoint: null,
        selectedSegment: null,
        showSegmentsPoints: false
    }),
    actions: {
        createPinElement(type) {
            const mapStore = useMaps()
            let config

            switch (type) {
                case 'red':
                    config = {
                        glyphText: '?',
                        background: '#FF0000',
                        borderColor: '#8B0000',
                        glyphColor: '#FFFFFF',
                        scale: 1
                    }
                    break
                case 'orange':
                    config = {
                        glyphText: 'ℹ',
                        background: '#FFA500',
                        borderColor: '#CC7700',
                        glyphColor: '#FFFFFF',
                        scale: 1
                    }
                    break
                case 'green':
                    config = {
                        glyphText: '✓',
                        background: '#22C55E',
                        borderColor: '#166534',
                        glyphColor: '#FFFFFF',
                        scale: 1.2
                    }
                    break
                default:
                    config = {
                        glyphText: '?',
                        background: '#FF0000',
                        borderColor: '#8B0000',
                        glyphColor: '#FFFFFF',
                        scale: 1
                    }
            }

            return new mapStore.google.maps.marker.PinElement(config)
        },
        createInteractionMarker(lat, lng, feature, isAlreadySegmented, angle) {
            const mapStore = useMaps()
            const type = isAlreadySegmented ? 'orange' : 'red'
            const pinElement = this.createPinElement(type)
            const title = isAlreadySegmented 
                ? i18n.global.t('Segmented: curve between these 3 points is too sharp.')
                : `${angle}°`
            
            const marker = new mapStore.google.maps.marker.AdvancedMarkerElement({
                position: { lat, lng },
                map: this.showSegmentsPoints ? toRaw(mapStore.map) : null,
                title: title,
                zIndex: 1000,
                gmpClickable: !isAlreadySegmented,
                content: pinElement
            })
            
            marker.segmentFeature = feature
            marker.uniqueId = uuid()
            marker.isAlreadySegmented = isAlreadySegmented
            
            return marker
        },
        showPointMarkers(features) {
            features.forEach(feature => {
                const pointGeom = feature.geometry?.type === 'GeometryCollection'
                    ? feature.geometry.geometries.find(g => g.type === 'Point')
                    : null
        
                if (!pointGeom) return
        
                const [lng, lat] = pointGeom.coordinates
                const angle = feature.properties.angle_degrees.toFixed(1)
                const hasGreenMarker = this.hasGreenMarkerAtLocation(lat, lng)
                
                if (hasGreenMarker) {
                    this.removeGreenMarkersAtLocation(lat, lng)
                }
        
                const marker = this.createInteractionMarker(
                    lat, 
                    lng, 
                    feature, 
                    hasGreenMarker, 
                    angle
                )
                this.interactionPoints.push(marker)
            })
        
            this.addListenersToMarkers()
        },
        addListenersToMarkers() {
            const mapStore = useMaps()

            this.interactionPoints.forEach((marker) => {
                // Skip adding listener for orange markers
                if (toRaw(marker).isAlreadySegmented) {
                    return
                }
                
                marker.addEventListener('gmp-click', async () => {
                    this.closeInfoWindows(marker)

                    const content = this.createInfoWindowContent(marker)

                    const infoWindow = new mapStore.google.maps.InfoWindow({
                        content: content,
                        position: marker.position
                    })

                    infoWindow.addListener('domready', () => {
                        const button = document.getElementById(`segment-btn-${marker.uniqueId}`)
                        if (button) {
                            button.onclick = async () => await this.segmentPoint(marker)
                        }
                    })

                    infoWindow.open({
                        map: toRaw(mapStore.map)
                    })
            
                    this.infoWindows.push({
                        marker: toRaw(marker),
                        infoWindow: infoWindow
                    })

                    this.drawLinesOfPoint(marker)
                })
            })
        },
        roundTo5Decimals(value) {
            // Round to 5 decimal places (~1.1 meters precision)
            return Math.round(value * 100000) / 100000
        },
        areCoordinatesEqual(coord1, coord2) {
            const rounded1 = [this.roundTo5Decimals(coord1[0]), this.roundTo5Decimals(coord1[1])]
            const rounded2 = [this.roundTo5Decimals(coord2[0]), this.roundTo5Decimals(coord2[1])]
            return rounded1[0] === rounded2[0] && rounded1[1] === rounded2[1]
        },
        areLineCoordsEqual(a, b) {
            if (!Array.isArray(a) || !Array.isArray(b)) return false
            if (a.length < 2 || b.length < 2) return false
            if (a.length !== b.length) return false

            const precision = 6 // 1.1 meters precision
            const aLine = turf.truncate(turf.lineString(a), { precision, mutate: false })
            const bLine = turf.truncate(turf.lineString(b), { precision, mutate: false })

            // same direction
            if (turf.booleanEqual(aLine, bLine)) return true

            // reversed direction
            const bRevLine = turf.truncate(turf.lineString([...b].reverse()), { precision, mutate: false })
            return turf.booleanEqual(aLine, bRevLine)
        },
        replaceSelectedLineInAllSegments(selectedLineCoords, newSegmentLinesGc) {
            if (!Array.isArray(selectedLineCoords) || selectedLineCoords.length < 2) return false
            if (!newSegmentLinesGc || newSegmentLinesGc.type !== 'GeometryCollection' || !Array.isArray(newSegmentLinesGc.geometries)) {
                return false
            }

            const collectMultiLineCoords = (gc) => {
                const out = []
                for (const g of gc.geometries) {
                    if (!g) continue
                    if (g.type === 'MultiLineString' && Array.isArray(g.coordinates)) {
                        out.push(...g.coordinates)
                    } else if (g.type === 'GeometryCollection' && Array.isArray(g.geometries)) {
                        out.push(...collectMultiLineCoords(g))
                    }
                }
                return out
            }

            const newLines = collectMultiLineCoords(newSegmentLinesGc)
            if (newLines.length === 0) return false

            for (let gcIdx = 0; gcIdx < this.allSegmentsLinesCoords.length; gcIdx++) {
                const gc = this.allSegmentsLinesCoords[gcIdx]
                if (!gc || gc.type !== 'GeometryCollection' || !Array.isArray(gc.geometries)) continue

                for (let geomIdx = 0; geomIdx < gc.geometries.length; geomIdx++) {
                    const geom = gc.geometries[geomIdx]
                    if (!geom || geom.type !== 'MultiLineString' || !Array.isArray(geom.coordinates)) continue

                    const coords = geom.coordinates
                    const lineIdx = coords.findIndex(line => this.areLineCoordsEqual(line, selectedLineCoords))
                    if (lineIdx === -1) continue

                    coords.splice(lineIdx, 1, ...newLines)
                    return true
                }
            }

            return false
        },
        hasMarkerAtLocation(lat, lng, options = {}) {
            const { markerArray, isAlreadySegmented = null } = options
            const roundedLat = this.roundTo5Decimals(lat)
            const roundedLng = this.roundTo5Decimals(lng)
            
            return markerArray.some(marker => {
                const rawMarker = toRaw(marker)
                const pos = rawMarker.position
                if (!pos) return false
                
                // Filter by isAlreadySegmented if specified
                if (isAlreadySegmented !== null && rawMarker.isAlreadySegmented !== isAlreadySegmented) {
                    return false
                }
                
                const markerLat = this.roundTo5Decimals(pos.lat)
                const markerLng = this.roundTo5Decimals(pos.lng)
                return markerLat === roundedLat && markerLng === roundedLng
            })
        },
        hasRedMarkerAtLocation(lat, lng) {
            return this.hasMarkerAtLocation(lat, lng, {
                markerArray: this.interactionPoints,
                isAlreadySegmented: false
            })
        },
        hasOrangeMarkerAtLocation(lat, lng) {
            return this.hasMarkerAtLocation(lat, lng, {
                markerArray: this.interactionPoints,
                isAlreadySegmented: true
            })
        },
        hasGreenMarkerAtLocation(lat, lng) {
            return this.hasMarkerAtLocation(lat, lng, {
                markerArray: this.okMarkers
            })
        },
        removeGreenMarkersAtLocation(lat, lng) {
            const roundedLat = this.roundTo5Decimals(lat)
            const roundedLng = this.roundTo5Decimals(lng)
            
            const markersToRemove = []
            
            this.okMarkers.forEach((greenMarker, idx) => {
                const greenPos = toRaw(greenMarker).position
                if (!greenPos) return
                const greenRoundedLat = this.roundTo5Decimals(greenPos.lat)
                const greenRoundedLng = this.roundTo5Decimals(greenPos.lng)
                
                if (greenRoundedLat === roundedLat && greenRoundedLng === roundedLng) {
                    markersToRemove.push(idx)
                }
            })
            
            // Remove in reverse order to maintain indices
            markersToRemove.reverse().forEach(idx => {
                const marker = this.okMarkers[idx]
                toRaw(marker).setMap(null)
                this.okMarkers.splice(idx, 1)
            })
        },
        createInfoWindowContent(marker) {
            const ds = useDrawingStore()

            const minAngle = ds.minAngle
            const maxAngle = ds.maxAngle
            const angle = marker.segmentFeature.properties.angle_degrees.toFixed(1)
            const uniqueId = marker.uniqueId

            return `
                <div style="padding: 12px; min-width: 220px; font-family: Arial, sans-serif;">
                    <div style="margin: 10px; color: #f59e0b; font-weight: 600; font-size: 16px;">
                        ${i18n.global.t('Should this corner be a segment boundary?')}
                    </div>
                    <div style="margin-bottom: 10px; padding: 8px; background: #f8f9fa; border-radius: 4px;">
                        <div style="color: #2c3e50; font-size: 14px; font-weight: 600; margin-bottom: 4px;">
                            ${i18n.global.t('Turn angle')}: ${angle}°
                        </div>
                        <div style="color: #7f8c8d; font-size: 12px;">
                            ${i18n.global.t('Review range')}: ${minAngle}° – ${maxAngle}°
                        </div>
                    </div>
                    <button id="segment-btn-${uniqueId}" style="background-color: #27ae60; color: white; padding: 10px 16px; border: none; border-radius: 4px; cursor: pointer; width: 100%; font-size: 14px; font-weight: 500;">
                        ${i18n.global.t('Yes, segment here')}
                    </button>
                </div>
            `
        },
        async segmentPoint(marker) {
            const feature = toRaw(marker).segmentFeature
            if (!feature?.geometry || feature.geometry.type !== 'GeometryCollection') {
                console.error('Invalid feature geometry')
                return
            }
        
            // Validate that selectedSegment contains LineString geometries
            if (!this.selectedSegment || this.selectedSegment.length === 0) {
                console.error('No LineString geometries found in selectedSegment')
                return
            }
        
            const segmentBtn = document.getElementById(`segment-btn-${toRaw(marker).uniqueId}`)
            if (segmentBtn) {
                segmentBtn.disabled = true
                segmentBtn.innerHTML = i18n.global.t('Segmenting...')
            }
        
            const ds = useDrawingStore()
            const data = {
                segment: this.selectedSegment,
                point: {
                    type: 'Point',
                    coordinates: [marker.position.lng, marker.position.lat]
                },
                angle_diff_upper_threshold: ds.maxAngle,
            }
            
            const res = await postSegmentLines(data)
            if (res) {
                let newSegmentLines = null
                if (res.segment_lines && res.segment_lines.type === 'GeometryCollection') {
                    const selectedLineCoords = this.selectedSegment?.[0]?.coordinates
                    const ok = this.replaceSelectedLineInAllSegments(selectedLineCoords, res.segment_lines)
                    if (!ok) {
                        console.error('Could not find selectedSegment line in allSegmentsLinesCoords; refusing to append new collection')
                        return
                    }
                    newSegmentLines = res.segment_lines
                } else {
                    console.error('Invalid segment_lines format')
                    return
                }

                // Update segmentFeature for remaining red markers on the same line
                if (newSegmentLines) {
                    this.updateRemainingMarkers(newSegmentLines)
                }

                this.removeMarker(marker)
                this.redrawAllSegments()
            }
        },
        closeInfoWindows(marker) {
            const rawMarker = toRaw(marker)

            this.infoWindows = this.infoWindows.filter(item => {
                if (toRaw(item.marker) === rawMarker) {
                    if (item.infoWindow) {
                        item.infoWindow.close()
                        item.infoWindow = null
                    }
                    return false
                }
                return true
            })

            // remove stroke from the polyline
            this.clearLinesOfPoint()
        },
        removeMarker(marker) {
            const mapStore = useMaps()
            const rawMarker = toRaw(marker)

            this.closeInfoWindows(marker)

            mapStore.google.maps.event.clearListeners(rawMarker, 'click')

            rawMarker.setMap(null)

            const index = this.interactionPoints.findIndex(m => toRaw(m) === rawMarker)
            if (index !== -1) {
                this.interactionPoints.splice(index, 1)
            }
        },
        drawLinesOfPoint(marker) {
            this.clearLinesOfPoint()
        
            const feature = marker.segmentFeature
            if (!feature?.geometry) {
                return
            }
        
            const mapStore = useMaps()

            let lineGeoms = []
        
            if (feature.geometry.type === 'GeometryCollection') {
                lineGeoms = feature.geometry.geometries.filter(g => g.type === 'LineString')
            }
        
            lineGeoms.forEach(geom => {
                const path = geom.coordinates.map(([lng, lat]) => ({ lat, lng }))
        
                const polyline = new mapStore.google.maps.Polyline({
                    path,
                    map: toRaw(mapStore.map),
                    zIndex: 2,
                    strokeWeight: 4
                })
        
                this.linesOfPoint.push(polyline)
                polyline.setMap(toRaw(mapStore.map))
            })
            this.selectedSegment = lineGeoms
        },
        updateRemainingMarkers(newSegmentLines) {
            this.interactionPoints.forEach(remainingMarker => {
                const rawMarker = toRaw(remainingMarker)
                if (rawMarker.isAlreadySegmented) return
                
                const markerFeature = rawMarker.segmentFeature
                if (!markerFeature) return
                
                // Find which new segment contains this marker
                const markerRounded = [this.roundTo5Decimals(rawMarker.position.lng), this.roundTo5Decimals(rawMarker.position.lat)]
                
                // Find the LineString that contains this marker
                for (const geom of newSegmentLines.geometries) {
                    if (geom.type === 'MultiLineString' && Array.isArray(geom.coordinates)) {
                        for (const lineCoords of geom.coordinates) {
                            if (!Array.isArray(lineCoords) || lineCoords.length === 0) continue
                            
                            // check if the marker is on the line
                            if (lineCoords.some(coord => this.areCoordinatesEqual(coord, markerRounded))) {
                                // Update segmentFeature but keep original properties
                                rawMarker.segmentFeature = {
                                    ...markerFeature,
                                    geometry: {
                                        type: 'GeometryCollection',
                                        geometries: [{
                                            type: 'LineString',
                                            coordinates: lineCoords
                                        }]
                                    }
                                }
                                return
                            }
                        }
                    }
                }
            })
        },
        processGeometryCollections(geomCollections) {
            const mapStore = useMaps()
            let colorIndex = 0
            let prevEndPoint = null
            const lines = []

            geomCollections.forEach(geomCollection => {
                if (!geomCollection || geomCollection.type !== 'GeometryCollection' || !Array.isArray(geomCollection.geometries)) {
                    return
                }

                geomCollection.geometries
                    .filter(g => g && g.type === 'MultiLineString' && Array.isArray(g.coordinates))
                    .forEach(multiLine => {
                        multiLine.coordinates.forEach(lineCoords => {
                            if (!Array.isArray(lineCoords) || lineCoords.length === 0) return

                            const path = lineCoords.map(([lng, lat]) => ({ lat, lng }))
                            const color = mapStore.randomRainbowColors[colorIndex % mapStore.randomRainbowColors.length]

                            const segmentLine = new mapStore.google.maps.Polyline({
                                path,
                                strokeColor: color,
                                strokeOpacity: 1,
                                strokeWeight: 2,
                                geodesic: true,
                                map: toRaw(mapStore.map),
                                zIndex: 3
                            })

                            lines.push(segmentLine)

                            // Add green OK marker where color changes
                            if (colorIndex > 0 && lineCoords.length >= 2) {
                                this.addJunctionOkMarker(prevEndPoint, lineCoords[1])
                            }

                            // Save last point of current line for next junction marker
                            prevEndPoint = lineCoords[lineCoords.length - 1]

                            colorIndex++
                        })
                    })
            })

            return { colorIndex, prevEndPoint, lines }
        },
        showAllSegmentsLines(result) {
            this.clearAllSegmentsLines()
            this.clearOkMarkers()
        
            const allSegmentsLines = []
            this.allSegmentsLinesCoords = []
        
            const headlandSegments = result?.headland_segments
            if (!headlandSegments || !Array.isArray(headlandSegments.geometries)) {
                return
            }

            this.shuffleColors(true)
        
            // Save coordinates first
            headlandSegments.geometries.forEach(geomCollection => {
                if (geomCollection && geomCollection.type === 'GeometryCollection') {
                    this.allSegmentsLinesCoords.push(geomCollection)
                }
            })
        
            // Process geometries and create lines
            const { colorIndex, prevEndPoint, lines } = this.processGeometryCollections(
                headlandSegments.geometries
            )
            
            allSegmentsLines.push(...lines)

            // Add OK marker for the last point
            if (colorIndex > 1 && prevEndPoint) {
                this.addLastOkMarker(prevEndPoint)
            }
        
            this.allSegmentsLines = allSegmentsLines
        },
        addOkMarker(lat, lng) {
            if (this.hasRedMarkerAtLocation(lat, lng) || this.hasOrangeMarkerAtLocation(lat, lng)) {
                return
            }
            
            const mapStore = useMaps()
            const pinElement = this.createPinElement('green')

            const marker = new mapStore.google.maps.marker.AdvancedMarkerElement({
                position: { lat, lng },
                map: this.showSegmentsPoints ? toRaw(mapStore.map) : null,
                title: i18n.global.t('Segmented'),
                zIndex: 999,
                content: pinElement
            })

            this.okMarkers.push(marker)
        },
        addJunctionOkMarker(prevEndPoint, nextPoint) {
            if (!prevEndPoint || !nextPoint) return
            
            const lat = prevEndPoint[1]
            const lng = prevEndPoint[0]
            this.addOkMarker(lat, lng)
        },
        addLastOkMarker(prevEndPoint) {
            if (!prevEndPoint) return
            
            const lat = prevEndPoint[1]
            const lng = prevEndPoint[0]
            this.addOkMarker(lat, lng)
        },
        clearMarkers() {
            const mapStore = useMaps()
            this.interactionPoints.forEach(marker => {
                const rawMarker = toRaw(marker)
                rawMarker.setMap(null)
                mapStore.google.maps.event.clearListeners(rawMarker, 'click')
                this.closeInfoWindows(marker)
            })
            this.interactionPoints = []
        },
        clearOkMarkers() {
            this.okMarkers.forEach(marker => {
                toRaw(marker).setMap(null)
            })
            this.okMarkers = []
        },
        clearAllSegmentsLines() {
            this.allSegmentsLines.forEach(line => {
                toRaw(line).setMap(null)
            })
            this.allSegmentsLines = []
        },
        clearLinesOfPoint() {
            this.linesOfPoint.forEach(line => {
                toRaw(line).setMap(null)
            })
            this.linesOfPoint = []
        },
        clearAll() {
            this.clearMarkers()
            this.clearAllSegmentsLines()
            this.clearOkMarkers()
            this.clearLinesOfPoint()

            this.interactionPointFeatures = []
            this.showSegmentsPoints = false
        },
        redrawAllSegments() {
            // clear lines and OK markers from the map (keep the coordinates)
            this.allSegmentsLines.forEach(line => {
                toRaw(line).setMap(null)
            })
            this.allSegmentsLines = []
            this.clearOkMarkers()
            
            if (!this.allSegmentsLinesCoords || this.allSegmentsLinesCoords.length === 0) {
                return
            }

            this.shuffleColors(false)
            
            // Process geometries and create lines
            const { colorIndex, prevEndPoint, lines } = this.processGeometryCollections(
                this.allSegmentsLinesCoords
            )
            
            this.allSegmentsLines = lines

            // Add OK marker for the last point
            if (colorIndex > 1 && prevEndPoint) {
                this.addLastOkMarker(prevEndPoint)
            }
        },
        shuffleColors(redraw) {
            const mapStore = useMaps()
            mapStore.randomRainbowColors = [...mapStore.randomRainbowColors].sort(() => Math.random() - 0.5)
            if (redraw) {
                this.redrawAllSegments()
            }
        },
        applySegmentsPointsVisibility() {
            const mapStore = useMaps()
            const map = this.showSegmentsPoints ? toRaw(mapStore.map) : null

            if (this.showSegmentsPoints && !this.interactionPoints.length && this.interactionPointFeatures.length) {
                this.showPointMarkers(this.interactionPointFeatures)
            } else {
                this.interactionPoints.forEach(marker => toRaw(marker).setMap(map))
                if (!map) {
                    this.interactionPoints.forEach(m => this.closeInfoWindows(m))
                }
            }

            this.okMarkers.forEach(marker => toRaw(marker).setMap(map))
        },
        toggleSegmentsPoints() {
            this.showSegmentsPoints = !this.showSegmentsPoints
            this.applySegmentsPointsVisibility()
        },
        handleStepChange(step, prevStep) {
            if (step === 3 && prevStep !== 3) {
                this.showSegmentsPoints = true
                this.applySegmentsPointsVisibility()
            } else if (prevStep === 3 && step !== 3) {
                this.showSegmentsPoints = false
                this.applySegmentsPointsVisibility()
            }
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useSegmentsStore, import.meta.hot))
}
